// Reads call_logs.cost (written by voxi-2-0's src/pricing.js) into the rows the call log views show.
//
// total_cost is the sum of every part, not only STT + LLM + TTS: it also holds the carrier's charge
// (telephony_cost, from the carrier's hangup webhook) and the decisions module's requests
// (decisions_cost: warm-up, barge-in checks). A part with no row of its own lands in "Other", so the
// rows always add up to the total.

export interface CallCostRow {
  label: string;
  cost: number;
  // second line under the label, e.g. "plivo · 210 s billed at $0.00475/min"
  detail?: string | null;
}

export interface CallCost {
  stt_cost: number;
  llm_cost: number;
  tts_cost: number;
  telephony_cost: number | null;
  telephony_detail: string | null;
  decisions_cost: number;
  decisions_detail: string | null;
  other_cost: number;
  total_cost: number;
}

export const EMPTY_CALL_COST: CallCost = {
  stt_cost: 0,
  llm_cost: 0,
  tts_cost: 0,
  telephony_cost: null,
  telephony_detail: null,
  decisions_cost: 0,
  decisions_detail: null,
  other_cost: 0,
  total_cost: 0,
};

const num = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

function telephonyDetail(telephony: any): string | null {
  if (!telephony || typeof telephony !== "object") return null;
  const parts: string[] = [];
  if (telephony.carrier) parts.push(String(telephony.carrier));
  const seconds = telephony.bill_duration_s;
  const rate = telephony.rate_per_minute;
  if (typeof seconds === "number" && typeof rate === "number") {
    parts.push(`${seconds} s billed at $${rate}/min`);
  } else if (typeof seconds === "number") {
    parts.push(`${seconds} s billed`);
  }
  return parts.length ? parts.join(" · ") : null;
}

/** Parses call_logs.cost (a JSON object, or its string form). Null/garbage gives zeros. */
export function parseCallCost(raw: unknown): CallCost {
  if (!raw) return { ...EMPTY_CALL_COST };
  let data: any;
  try {
    data = typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch {
    return { ...EMPTY_CALL_COST };
  }
  if (!data || typeof data !== "object") return { ...EMPTY_CALL_COST };

  const stt = num(data.stt_cost);
  const llm = num(data.llm_cost);
  const tts = num(data.tts_cost);
  const telephony =
    typeof data.telephony_cost === "number" && Number.isFinite(data.telephony_cost)
      ? data.telephony_cost
      : null;
  const decisions = num(data.decisions_cost ?? data.decisions?.cost);
  const total = num(data.total_cost);

  // Whatever the total holds beyond the named parts (e.g. a realtime model's cost).
  // Below half a display unit ($0.00005) it is rounding, not a part.
  const rest = total - (stt + llm + tts + (telephony ?? 0) + decisions);
  const other = rest >= 0.00005 ? rest : 0;

  return {
    stt_cost: stt,
    llm_cost: llm,
    tts_cost: tts,
    telephony_cost: telephony,
    telephony_detail: telephonyDetail(data.telephony),
    decisions_cost: decisions,
    decisions_detail:
      data.decisions?.purposes && typeof data.decisions.purposes === "object"
        ? Object.keys(data.decisions.purposes).filter((p) => p !== "warm-up").join(", ") || null
        : null,
    other_cost: other,
    total_cost: total,
  };
}

/** The rows to show. STT, LLM and TTS always; telephony when the carrier reported it; the rest when non-zero. */
export function callCostRows(
  cost: CallCost,
  models?: { stt?: string | null; llm?: string | null; tts?: string | null } | null,
): CallCostRow[] {
  const rows: CallCostRow[] = [
    { label: "Speech-to-Text", cost: cost.stt_cost, detail: models?.stt },
    { label: "LLM Processing", cost: cost.llm_cost, detail: models?.llm },
    { label: "Text-to-Speech", cost: cost.tts_cost, detail: models?.tts },
  ];
  if (cost.telephony_cost !== null) {
    rows.push({ label: "Telephony", cost: cost.telephony_cost, detail: cost.telephony_detail });
  }
  if (cost.decisions_cost > 0) {
    rows.push({ label: "Decisions", cost: cost.decisions_cost, detail: cost.decisions_detail });
  }
  if (cost.other_cost > 0) {
    rows.push({ label: "Other", cost: cost.other_cost });
  }
  return rows;
}
